"use client";

import {
  axisEvery,
  dayAtX,
  peakAnchor,
  stepDay,
  valueLabelY,
  type AltitudeChartModel,
} from "@tripcart/shared";

import { cx } from "../../cx";
import styles from "./altitude-chart.module.css";

/** Past this many points, only the peak is labelled; a phone fits fewer. */
const LABEL_EVERY_POINT_MAX = 12;
const LABEL_EVERY_POINT_MAX_NARROW = 7;

function GridLines({
  scale,
  unit,
}: Pick<AltitudeChartModel, "scale" | "unit">) {
  const { W, R, L, T, H, B } = scale.frame;
  const IH = H - T - B;
  const values: number[] = [];
  for (let v = 0; v <= scale.top; v += scale.step) values.push(v);
  return values.map((v) => {
    const gy = T + IH - (v / scale.top) * IH;
    return (
      <g key={v}>
        <line className={styles.grid} x1={L} x2={W - R} y1={gy} y2={gy} />
        <text className={styles.axis} x={L - 8} y={gy + 3.5} textAnchor="end">
          {v.toLocaleString("en-US")}
          {v === 0 ? ` ${unit}` : ""}
        </text>
      </g>
    );
  });
}

/** Every point's altitude on a short tour; the highest point always, by name. */
function PointLabels({
  route,
  scale,
  peak,
  fmt,
  unit,
  narrow,
}: AltitudeChartModel & { narrow: boolean }) {
  const max = narrow ? LABEL_EVERY_POINT_MAX_NARROW : LABEL_EVERY_POINT_MAX;
  const peakX = scale.x(peak);
  const anchor = peakAnchor(peakX, scale.frame);
  return (
    <>
      {route.length <= max
        ? route.map((p, i) =>
            p === peak ? null : (
              <text
                key={i}
                className={styles.value}
                x={scale.x(p)}
                y={valueLabelY(route, i, scale)}
                textAnchor="middle"
              >
                {fmt(p.m)}
              </text>
            ),
          )
        : null}
      <text
        className={styles.peak}
        x={peakX}
        y={scale.y(peak.m) - 11}
        textAnchor={anchor}
      >
        {peak.place} · {fmt(peak.m)} {unit}
      </text>
    </>
  );
}

/** Day labels exactly as typed; thinned to about seven on a narrow chart,
 *  always keeping the last day (spec §5.5). */
function DayAxis({
  days,
  scale,
  active,
  narrow,
}: Pick<AltitudeChartModel, "days" | "scale"> & {
  active: number;
  narrow: boolean;
}) {
  const n = days.length;
  const every = axisEvery(n, narrow);
  const { H } = scale.frame;
  return days.map((d, i) =>
    i % every && i !== n - 1 ? null : (
      <text
        key={i}
        className={cx(styles.axis, i === active && styles.axisOn)}
        x={scale.colX(i + 0.5)}
        y={H - 12}
        textAnchor="middle"
      >
        {d.day}
      </text>
    ),
  );
}

/** The active day's band, the filled area, the line, and one dot per stop. */
function RouteLayer({
  model,
  active,
}: {
  model: AltitudeChartModel;
  active: number;
}) {
  const { route, scale, line, area } = model;
  const { T, H, B } = scale.frame;
  const IH = H - T - B;
  return (
    <>
      {active >= 0 ? (
        <rect
          className={styles.band}
          x={scale.colX(active)}
          y={T}
          width={scale.colW}
          height={IH}
        />
      ) : null}
      <path className={styles.area} d={area} />
      <path className={styles.line} d={line} />
      {route.map((p, i) => (
        <circle
          key={i}
          className={styles.point}
          cx={scale.x(p)}
          cy={scale.y(p.m)}
          r={p.day === active ? 6 : 3.5}
        />
      ))}
    </>
  );
}

export function ChartSvg({
  model,
  active,
  narrow,
  ariaLabel,
  onActive,
}: {
  model: AltitudeChartModel;
  active: number;
  narrow: boolean;
  ariaLabel: string;
  onActive: (next: number) => void;
}) {
  const { days, scale } = model;
  const { W, H, L, R, T, B } = scale.frame;
  const IH = H - T - B;
  const n = days.length;

  const dayAt = (ev: React.PointerEvent<SVGRectElement>) => {
    const r = ev.currentTarget.ownerSVGElement!.getBoundingClientRect();
    return dayAtX(((ev.clientX - r.left) * W) / r.width, n, scale);
  };
  const onKeyDown = (ev: React.KeyboardEvent) => {
    const next = stepDay(ev.key, active, n);
    if (next === null) return;
    onActive(next);
    ev.preventDefault();
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      onBlur={() => onActive(-1)}
      onPointerLeave={() => onActive(-1)}
    >
      <GridLines scale={scale} unit={model.unit} />
      <RouteLayer model={model} active={active} />
      <PointLabels {...model} narrow={narrow} />
      <DayAxis days={days} scale={scale} active={active} narrow={narrow} />
      <rect
        x={L}
        y={T}
        width={W - L - R}
        height={IH}
        fill="transparent"
        onPointerMove={(ev) => onActive(dayAt(ev))}
        onPointerDown={(ev) => onActive(dayAt(ev))}
      />
    </svg>
  );
}
